import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Spinner } from 'react-bootstrap';
import AppointmentStatusSelect from './AppointmentStatusSelect';
import AppointmentStatus from './AppointmentStatus';
import { warnAlert, errorAlert } from '../../alert/defaultAlert';
import axiosInstance from '../../../../services/AxiosInstance';
import Api from '../../../../constants/Api';
import AsteriskRequired from '../../Common/AsteriskRequired';
import CancelButton from '../../Forms/Element/CancelButton';
import TextArea from '../../Forms/Element/TextArea';
import FormSection from '../../Common/Form/FormSection';
import './AppointmentUpdateStatus.css';

const getStatusLabel = (status, t) => {
    switch (status) {
        case AppointmentStatus.SCHEDULED:
            return t('scheduled');
        case AppointmentStatus.CONFIRMED:
            return t('confirmed');
        case AppointmentStatus.IN_PROGRESS:
            return t('in_progress');
        case AppointmentStatus.COMPLETED:
            return t('completed');
        case AppointmentStatus.CANCELED:
            return t('canceled');
        case AppointmentStatus.NO_SHOW:
            return t('no_show');
        default:
            return status;
    }
};

const AppointmentUpdateStatus = ({
    selectedRows,
    onUpdateStatus,
    onCancel
}) => {
    const { t } = useTranslation();
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [appointments, setAppointments] = useState([]);
    const [loadingAppointments, setLoadingAppointments] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [cancellationReason, setCancellationReason] = useState('');

    useEffect(() => {
        const loadAppointments = async () => {
            if (selectedRows.length === 0) {
                setAppointments([]);
                setSelectedStatus(null);
                setCancellationReason('');
                return;
            }

            setLoadingAppointments(true);
            try {
                const appointmentPromises = selectedRows.map((appointmentId) =>
                    axiosInstance.get(`${Api.appointment.appointment}/${appointmentId}`)
                );

                const responses = await Promise.all(appointmentPromises);
                setAppointments(responses.map((response) => response.data));
            } catch (error) {
                errorAlert(error);
                setAppointments([]);
            } finally {
                setLoadingAppointments(false);
            }
        };

        loadAppointments();
    }, [selectedRows]);

    const statusBreakdown = useMemo(() => {
        return appointments.reduce((acc, appointment) => {
            const key = appointment.appointmentStatus || 'UNKNOWN';
            acc[key] = (acc[key] || 0) + 1;
            return acc;
        }, {});
    }, [appointments]);

    const summaryText = useMemo(() => {
        const entries = Object.entries(statusBreakdown);
        if (!entries.length) {
            return t('loading_appointments');
        }

        return entries
            .map(([status, count]) => `${count} ${getStatusLabel(status, t)}`)
            .join(' • ');
    }, [statusBreakdown, t]);

    const handleUpdateStatus = () => {
        if (!selectedStatus) {
            warnAlert(t('validation.required.status'));
            return;
        }

        if (selectedRows.length === 0) {
            warnAlert(t('validation.required.appointments'));
            return;
        }

        if (selectedStatus.value === AppointmentStatus.CANCELED && !cancellationReason.trim()) {
            warnAlert(t('validation.required.cancellation_reason'));
            return;
        }

        setShowConfirmModal(true);
    };

    const handleConfirmUpdate = async () => {
        if (!selectedStatus || selectedRows.length === 0) {
            return;
        }

        const reasonParam =
            selectedStatus.value === AppointmentStatus.CANCELED ? cancellationReason : null;

        setIsSubmitting(true);

        try {
            await onUpdateStatus(selectedRows, selectedStatus.value, reasonParam);
            setShowConfirmModal(false);
            setSelectedStatus(null);
            setCancellationReason('');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancelConfirm = () => {
        setShowConfirmModal(false);
        setSelectedStatus(null);
        setCancellationReason('');
        onCancel();
    };

    const handleCancel = () => {
        setSelectedStatus(null);
        setCancellationReason('');
        onCancel();
    };

    return (
        <div className={`appointment-status-modal ${showConfirmModal ? 'is-confirming' : ''}`}>
            {selectedRows.length === 0 ? (
                <div className="appointment-status-modal__empty">
                    <div>
                        <i className="fas fa-exclamation-circle fs-2 text-warning mb-2"></i>
                        <h6>{t('no_appointments_selected')}</h6>
                        <p className="mb-3">{t('please_select_appointments_first')}</p>
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={handleCancel}
                        >
                            <i className="fas fa-times me-2"></i>
                            {t('close')}
                        </button>
                    </div>
                </div>
            ) : showConfirmModal ? (
                <div className="appointment-status-modal__confirm appointment-status-modal__confirm--inline">
                    <div className="appointment-status-modal__confirm-copy">
                        <h6>{t('confirm_status_update')}</h6>
                        <p>
                            {t('confirm_status_change_detailed', {
                                count: selectedRows.length,
                                status: selectedStatus?.label || ''
                            })}
                        </p>
                    </div>

                    <div className="appointment-status-modal__confirm-summary">
                        <small>{selectedRows.length} agendamento(s) serão atualizados.</small>
                    </div>

                    <div className="appointment-status-modal__actions appointment-status-modal__actions--confirm">
                        <CancelButton onClick={handleCancelConfirm} disabled={isSubmitting}>
                            {t('cancel')}
                        </CancelButton>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleConfirmUpdate}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    {t('loading')}
                                </>
                            ) : (
                                t('confirm_update')
                            )}
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    {loadingAppointments && (
                        <div className="appointment-status-modal__loading">
                            <div>
                                <Spinner animation="border" variant="primary" size="sm" className="me-2" />
                                {t('loading_appointments')}
                            </div>
                        </div>
                    )}

                    <FormSection
                        compact
                        className="appointment-status-modal__section"
                        title={t('new_status')}
                        description={t('select_new_status')}
                        icon="fa-solid fa-arrows-rotate"
                    >
                        <div className="appointment-status-modal__next-status">
                            <Form.Label className="appointment-status-modal__next-status-label">
                                <strong>{t('status')}</strong> <AsteriskRequired />
                            </Form.Label>
                            <AppointmentStatusSelect
                                value={selectedStatus}
                                onChange={(option) => {
                                    setSelectedStatus(option);
                                    if (option?.value !== AppointmentStatus.CANCELED) {
                                        setCancellationReason('');
                                    }
                                }}
                                isClearable={false}
                                placeholder={t('select_new_status')}
                            />
                            {!selectedStatus && (
                                <small className="appointment-status-modal__helper">
                                    {t('validation.required.status')}
                                </small>
                            )}
                        </div>

                        {selectedStatus?.value === AppointmentStatus.CANCELED && (
                            <div className="appointment-status-modal__reason">
                                <Form.Label>
                                    <strong>{t('cancellation_reason')}:</strong> <AsteriskRequired />
                                </Form.Label>
                                <TextArea
                                    value={cancellationReason}
                                    onChange={(event) => setCancellationReason(event.target.value)}
                                    placeholder={t('cancellation_reason_placeholder')}
                                    rows={3}
                                    maxLength={255}
                                    counter="true"
                                />
                                {!cancellationReason.trim() && (
                                    <div className="appointment-status-modal__reason-text">
                                        {t('validation.required.cancellation_reason')}
                                    </div>
                                )}
                            </div>
                        )}
                    </FormSection>

                    <div className="appointment-status-modal__actions">
                        <CancelButton onClick={handleCancel}>
                            {selectedStatus ? t('cancel') : t('close')}
                        </CancelButton>

                        {selectedStatus && (
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleUpdateStatus}
                            >
                                <i className="fas fa-sync-alt me-2"></i>
                                {t('update_status')}
                            </button>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default AppointmentUpdateStatus;
